"use client";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  Button,
  Container,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  Link,
} from "@mui/material";
import { Email, LocationOn, Send, Business } from "@mui/icons-material";
import { toast } from "react-toastify";
import Header from "../components/HomepageContent/Header";
import Footer from "../components/HomepageContent/Footer";
import WarningModal from "@/components/WarningModal";
import authenticationApi from "@/lib/authenticationApi";

type ContactFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  message: string;
};

function formatUSPhone(raw: string): string {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const PRIMARY = "#153C7F";
const PRIMARY_LIGHT = "rgba(21, 60, 127, 0.08)";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(0,0,0,0.24)" },
    "&.Mui-focused fieldset": { borderColor: PRIMARY, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: PRIMARY },
};

export default function Contact() {
  useEffect(() => {
    document.title = "CareAscendium - Contact";
  }, []);
  const [modalOpen, setModalOpen] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      companyName: "",
      message: "",
    },
  });

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    try {
      await authenticationApi.post("/v1/contact/send", {
        p_first_name: data.firstName.trim(),
        p_last_name: data.lastName.trim(),
        p_email: data.email.trim(),
        p_phone: data.phone.trim(),
        p_company_name: data.companyName.trim(),
        p_message: data.message.trim(),
      });
      reset();
      setModalOpen(true);
    } catch (err: unknown) {
      const e = err as {
        details?: { message?: string; error_message?: string };
        message?: string;
      };
      const errorMessage =
        e?.details?.message ||
        e?.details?.error_message ||
        e?.message ||
        "Failed to send message. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fafafa",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      {/* Hero */}
      <Box
        sx={{
          bgcolor: PRIMARY,
          py: { xs: 6, md: 8 },
          px: 2,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage:
              "radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)",
            backgroundSize: "50px 50px",
          }}
        ></Box>
        <Container maxWidth="md">
          <Typography
            component="h1"
            sx={{
              color: "white",
              fontWeight: 700,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "2.75rem" },
              letterSpacing: "-0.02em",
              mb: 1.5,
            }}
          >
            Get in Touch
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.88)",
              fontSize: { xs: "1rem", md: "1.125rem" },
              maxWidth: 560,
              mx: "auto",
              lineHeight: 1.5,
            }}
          >
            Ready to transform your skilled nursing facility? We typically
            respond within 24 hours.
          </Typography>
        </Container>
      </Box>

      {/* Form + Contact */}
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, sm: 3 } }}
      >
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
          {/* Form */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              id="contact-form"
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                height: "100%",
              }}
            >
              <Typography
                component="h2"
                sx={{
                  fontSize: "1.35rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                  mb: 3,
                }}
              >
                Send a message
              </Typography>

              <Box
                component="form"
                sx={{ mt: 3 }}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="firstName"
                      control={control}
                      rules={{
                        required: "First Name is required",
                        validate: (v) => {
                          const trimmed = (v || "").trim();
                          if (!trimmed) return "First Name is required";
                          if (!/^[a-zA-Z\s\-'.]+$/.test(trimmed))
                            return "First Name should contain only letters";
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          type="text"
                          label="First Name"
                          variant="outlined"
                          autoComplete="given-name"
                          onChange={(e) =>
                            field.onChange(e.target.value.replace(/[0-9]/g, ""))
                          }
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          sx={textFieldSx}
                          inputProps={{
                            inputMode: "text",
                            "aria-label": "First Name",
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="lastName"
                      control={control}
                      rules={{
                        required: "Last Name is required",
                        validate: (v) => {
                          const trimmed = (v || "").trim();
                          if (!trimmed) return "Last Name is required";
                          if (!/^[a-zA-Z\s\-'.]+$/.test(trimmed))
                            return "Last Name should contain only letters";
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          type="text"
                          label="Last Name"
                          variant="outlined"
                          autoComplete="family-name"
                          onChange={(e) =>
                            field.onChange(e.target.value.replace(/[0-9]/g, ""))
                          }
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          sx={textFieldSx}
                          inputProps={{
                            inputMode: "text",
                            "aria-label": "Last Name",
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Email Address"
                      variant="outlined"
                      autoComplete="email"
                      {...register("email", {
                        required: "Email Address is required",
                        validate: (v) =>
                          v?.trim() ? true : "Email Address is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Invalid email format",
                        },
                      })}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Phone Number"
                          variant="outlined"
                          type="tel"
                          placeholder="(555) 123-4567"
                          onChange={(e) =>
                            field.onChange(formatUSPhone(e.target.value))
                          }
                          error={!!errors.phone}
                          helperText={errors.phone?.message}
                          sx={textFieldSx}
                          inputProps={{
                            inputMode: "tel",
                            "aria-label": "Phone Number",
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      type="text"
                      label="Company/Facility Name"
                      variant="outlined"
                      autoComplete="organization"
                      {...register("companyName")}
                      error={!!errors.companyName}
                      helperText={errors.companyName?.message}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      required
                      label="Message"
                      multiline
                      rows={4}
                      variant="outlined"
                      {...register("message", {
                        required: "Message is required",
                        validate: (v) =>
                          v?.trim() ? true : "Message is required",
                      })}
                      error={!!errors.message}
                      helperText={errors.message?.message}
                      sx={textFieldSx}
                    />
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Send sx={{ fontSize: 20 }} />
                    )
                  }
                  sx={{
                    mt: 3,
                    py: 1.75,
                    bgcolor: PRIMARY,
                    color: "white",
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#0F2A5A", transform: "translateY(-2px)" },
                    transition: "all 0.3s ease",
                  }}
                >
                  SUBMIT
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Contact & company */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                height: "100%",
                bgcolor: PRIMARY_LIGHT,
              }}
            >
              <Typography
                component="h2"
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                  mb: 3,
                }}
              >
                Contact & company
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 600, color: "#1a1a1a", mb: 0.5 }}>
                  Bharath Bynagari
                </Typography>
                <Typography variant="body2" sx={{ color: "#555", mb: 2 }}>
                  Founder & CEO
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Email sx={{ fontSize: 20, color: PRIMARY }} />
                  <Link
                    component="a"
                    href="mailto:bynagari@careascendium.com"
                    underline="none"
                    sx={{
                      color: PRIMARY,
                      fontWeight: 500,
                      position: "relative",
                      zIndex: 1,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    bynagari@careascendium.com
                  </Link>
                </Box>
              </Box>

              <Box
                sx={{
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  pt: 3,
                }}
              >
                <Typography sx={{ fontWeight: 600, color: "#1a1a1a", mb: 1 }}>
                  CAREASCENDIUM, LLC.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#555", mb: 2, lineHeight: 1.5 }}
                >
                  Dedicated to transforming skilled nursing facility operations
                  through innovative technology solutions.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <LocationOn sx={{ fontSize: 20, color: PRIMARY }} />
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    Carmel, IN
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      {/* <Box
        sx={{
          py: 12,
          background: "linear-gradient(135deg, #153C7F 0%, #1E4A8C 100%)",
          color: "white",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage:
              "radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)",
            backgroundSize: "50px 50px",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Ready to Get Started?
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}
          >
            Schedule a personalized demonstration and see how CareAscendium can
            transform your facility operations
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<Send />}
            onClick={() => {
              // Scroll to the contact form
              document
                .getElementById("contact-form")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            sx={{
              bgcolor: "white",
              color: "#153C7F",
              px: 6,
              py: 3,
              fontSize: "1.2rem",
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(255,255,255,0.3)",
              "&:hover": {
                bgcolor: "#f5f5f5",
                boxShadow: "0 6px 20px rgba(255,255,255,0.4)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Schedule a Demo
          </Button>
        </Container>
      </Box> */}

      {/* Success Modal */}
      <WarningModal
        open={modalOpen}
        onClose={handleCloseModal}
        type="success"
        title="Message Sent Successfully!"
        message="Thank you for your interest! We will contact you within 24 hours to schedule your demo."
        primaryButtonText="OK"
        onPrimary={handleCloseModal}
      />
      <Footer />
    </Box>
  );
}
